import categoryHist from "../../assets/zubroteka/category_hist.svg";
import categoryBio from "../../assets/zubroteka/category_bio.svg";
import categoryGeo from "../../assets/zubroteka/category_geo.svg";
import categoryMath from "../../assets/zubroteka/category_math.svg";
import categoryLangs from "../../assets/zubroteka/category_langs.svg";

export const QUICK_FILTER_CATEGORIES = [
  {
    id: "history",
    color: "#8B430F",
    labelKey: "quiz.quickFilter.history",
    icon: categoryHist,
    // Prefer LT canonical first — anonymous get-filters is forbidden, so public
    // fallback uses the first alias as mainSubject for search.
    subjectAliases: ["Istorija", "History", "Historia", "Historia, Sociales y Ciudadanas"],
  },
  {
    id: "biology",
    color: "#3A47D9",
    labelKey: "quiz.quickFilter.biology",
    icon: categoryBio,
    subjectAliases: ["Biologija", "Biología"],
  },
  {
    id: "geography",
    color: "#FFCF24",
    labelKey: "quiz.quickFilter.geography",
    icon: categoryGeo,
    subjectAliases: ["Geografija"],
  },
  {
    id: "mathematics",
    color: "#9747FF",
    labelKey: "quiz.quickFilter.mathematics",
    icon: categoryMath,
    subjectAliases: ["Maths"],
  },
  {
    id: "languages",
    color: "#009A44",
    labelKey: "quiz.quickFilter.languages",
    icon: categoryLangs,
    subjectAliases: ["Lietuvių kalba"],
  },
];

/** Used when get-filters is unavailable to anonymous users (403). */
export const PUBLIC_FALLBACK_GRADES = ["9", "10", "11", "12"];

const GRADE_RANGE_PATTERN = /^(\d+)-(\d+)$/;

function normalizeFilterValue(value) {
  return String(value || "").trim().toLowerCase();
}

function gradeSortKey(grade) {
  const trimmed = String(grade || "").trim();
  const rangeMatch = trimmed.match(GRADE_RANGE_PATTERN);
  if (rangeMatch) {
    return [Number(rangeMatch[1]), Number(rangeMatch[2])];
  }

  const single = Number.parseInt(trimmed, 10);
  if (!Number.isNaN(single)) {
    return [single, single];
  }

  return [0, 0];
}

export function sortSchoolGrades(grades = []) {
  return [...grades]
    .filter((grade) => String(grade || "").trim())
    .sort((a, b) => {
      const [aStart, aEnd] = gradeSortKey(a);
      const [bStart, bEnd] = gradeSortKey(b);
      if (aStart !== bStart) return aStart - bStart;
      if (aEnd !== bEnd) return aEnd - bEnd;
      return String(a).localeCompare(String(b));
    });
}

export function matchesSchoolGradeFilter(schoolGrades, selectedGrade) {
  if (!selectedGrade) return true;
  if (!Array.isArray(schoolGrades)) return false;
  return schoolGrades
    .filter((grade) => String(grade || "").trim())
    .includes(selectedGrade);
}

/**
 * Resolve curated category aliases against API subject list.
 * Returns categories that have at least one matching subject, with resolvedSubjects set.
 */
export function resolveVisibleCategories(availableSubjects = []) {
  const availableByKey = {};
  for (const subject of availableSubjects) {
    if (typeof subject !== "string" || !subject.trim()) continue;
    availableByKey[normalizeFilterValue(subject)] = subject;
  }

  return QUICK_FILTER_CATEGORIES.map((category) => {
    const resolvedSubjects = [];
    for (const alias of category.subjectAliases) {
      const resolved = availableByKey[normalizeFilterValue(alias)];
      if (resolved && !resolvedSubjects.includes(resolved)) {
        resolvedSubjects.push(resolved);
      }
    }
    if (resolvedSubjects.length === 0) return null;
    return { ...category, resolvedSubjects };
  }).filter(Boolean);
}

/** Show all curated tiles using primary aliases (public anonymous fallback). */
export function getPublicFallbackCategories() {
  return QUICK_FILTER_CATEGORIES.map((category) => ({
    ...category,
    resolvedSubjects: [category.subjectAliases[0]],
  }));
}

export function collectFiltersFromDecks(decks = []) {
  const subjects = [];
  const grades = [];
  const seenSubjects = new Set();
  const seenGrades = new Set();

  for (const deck of decks) {
    const subject = deck?.mainSubject;
    if (typeof subject === "string" && subject.trim()) {
      const key = normalizeFilterValue(subject);
      if (!seenSubjects.has(key)) {
        seenSubjects.add(key);
        subjects.push(subject.trim());
      }
    }
    for (const grade of deck?.schoolGrades || []) {
      if (typeof grade !== "string" || !grade.trim()) continue;
      const key = grade.trim();
      if (!seenGrades.has(key)) {
        seenGrades.add(key);
        grades.push(key);
      }
    }
  }

  return { subjects, grades: sortSchoolGrades(grades) };
}

export function parseFiltersResponse(response) {
  const data = response?.data ?? response ?? {};
  const subjects = Array.isArray(data.subjects) ? data.subjects : [];
  const grades = Array.isArray(data.grades) ? data.grades : [];
  return {
    subjects: subjects.filter((s) => typeof s === "string" && s.trim()),
    grades: sortSchoolGrades(grades),
  };
}
